import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Shield, 
  Clock, 
  Car, 
  Phone, 
  Mail, 
  MapPin,
  CheckCircle,
  ArrowRight,
  Star
} from "lucide-react";

const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_rentflow-67/artifacts/zjs6jlz1_ChatGPT%20Image%20Feb%201%2C%202026%2C%2007_47_29%20PM.png";

export default function HomePage() {
  const navigate = useNavigate();
  const [isHovered, setIsHovered] = useState(false);

  const features = [
    {
      icon: Shield,
      title: "Insurance Specialists",
      description: "We work directly with all major insurance companies to get you back on the road fast."
    },
    {
      icon: Clock,
      title: "24/7 Service",
      description: "Available around the clock, 7 days a week. We're here whenever you need us, day or night."
    },
    {
      icon: Car,
      title: "GTA-Wide Delivery",
      description: "We deliver vehicles anywhere in the Greater Toronto Area, anytime. Convenience at your doorstep."
    }
  ];

  const benefits = [
    "Direct billing to insurance companies",
    "No upfront costs in most cases",
    "Wide selection of replacement vehicles",
    "Flexible rental periods",
    "24/7 customer support",
    "Free delivery anywhere in the GTA"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-lg border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-24">
            <div className="flex items-center gap-3">
              <img 
                src={COMPANY_LOGO} 
                alt="AK Car Rental" 
                className="h-20 w-auto object-contain drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]"
              />
            </div>
            <div className="flex items-center gap-4">
              <a href="tel:365-366-2379" className="hidden sm:flex items-center gap-2 text-white/70 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                <span>365-366-2379</span>
              </a>
              <Button 
                onClick={() => navigate('/login')}
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-6"
              >
                Portal Login
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center">
            {/* Logo */}
            <div 
              className="inline-block mb-8 transform transition-transform duration-500 hover:scale-105"
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            >
              <img 
                src={COMPANY_LOGO} 
                alt="AK Car Rental" 
                className={`h-48 sm:h-56 w-auto object-contain mx-auto transition-all duration-500 drop-shadow-[0_0_25px_rgba(255,255,255,0.6)] ${isHovered ? 'drop-shadow-[0_0_40px_rgba(255,255,255,0.8)]' : ''}`}
              />
            </div>

            {/* Tagline */}
            <div className="mb-6 flex flex-wrap items-center justify-center gap-3">
              <span className="inline-block px-4 py-2 bg-amber-500/20 text-amber-400 rounded-full text-sm font-medium border border-amber-500/30">
                Insurance Replacement Specialists
              </span>
              <span className="inline-block px-4 py-2 bg-emerald-500/20 text-emerald-400 rounded-full text-sm font-medium border border-emerald-500/30">
                24/7 Service
              </span>
              <span className="inline-block px-4 py-2 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium border border-blue-500/30">
                GTA-Wide Delivery
              </span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Your Trusted Partner for
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">
                Insurance Replacement Vehicles
              </span>
            </h1>

            {/* Subheadline */}
            <p className="text-xl text-white/60 max-w-3xl mx-auto mb-10 leading-relaxed">
              Specializing in hassle-free vehicle rentals for insurance claims. 
              We handle the paperwork so you can get back on the road quickly and stress-free.
              <span className="block mt-2 text-amber-400 font-medium">
                Available 24/7 with delivery anywhere in the Greater Toronto Area!
              </span>
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button 
                size="lg"
                className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8 py-6 text-lg group"
                onClick={() => window.location.href = 'tel:365-366-2379'}
              >
                <Phone className="w-5 h-5 mr-2" />
                Call Now: 365-366-2379
                <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
                onClick={() => window.location.href = 'mailto:akcarrental01@gmail.com'}
              >
                <Mail className="w-5 h-5 mr-2" />
                Email Us
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Choose AK Car Rental?
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              We specialize exclusively in insurance replacement vehicles, making the process seamless for you.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card 
                key={index}
                className="bg-slate-800/50 border-white/10 hover:border-amber-500/50 transition-all duration-300 hover:transform hover:-translate-y-2 group"
              >
                <CardContent className="p-8 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-amber-500/20 text-amber-400 mb-6 group-hover:bg-amber-500/30 transition-colors">
                    <feature.icon className="w-8 h-8" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">
                    {feature.title}
                  </h3>
                  <p className="text-white/60 leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Insurance Claims Made Simple
              </h2>
              <p className="text-white/60 text-lg mb-8 leading-relaxed">
                At AK Car Rental, we understand how stressful it can be when you're dealing with an insurance claim. 
                That's why we've streamlined our entire process to work seamlessly with insurance companies, 
                getting you into a reliable replacement vehicle as quickly as possible.
              </p>
              
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <CheckCircle className="w-4 h-4 text-amber-400" />
                    </div>
                    <span className="text-white/80">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-blue-500/20 rounded-3xl blur-2xl"></div>
              <Card className="relative bg-slate-800/80 border-white/10 overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="flex">
                      {[1,2,3,4,5].map((star) => (
                        <Star key={star} className="w-5 h-5 text-amber-400 fill-amber-400" />
                      ))}
                    </div>
                    <span className="text-white/60 text-sm">Trusted by customers</span>
                  </div>
                  <blockquote className="text-white text-lg italic mb-6">
                    "AK Car Rental made my insurance claim process so easy. They handled everything with my insurance company and I was driving a great replacement car within hours!"
                  </blockquote>
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                      <span className="text-amber-400 font-semibold">JD</span>
                    </div>
                    <div>
                      <p className="text-white font-medium">Happy Customer</p>
                      <p className="text-white/60 text-sm">Hamilton, ON</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-amber-500/10 via-slate-900 to-amber-500/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Need an Insurance Replacement Vehicle?
          </h2>
          <p className="text-white/60 text-lg mb-8">
            Contact us today and let us handle the hassle. We'll work directly with your insurance company 
            to get you back on the road as quickly as possible.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              size="lg"
              className="bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-8 py-6 text-lg"
              onClick={() => window.location.href = 'tel:365-366-2379'}
            >
              <Phone className="w-5 h-5 mr-2" />
              365-366-2379
            </Button>
            <Button 
              size="lg"
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 px-8 py-6 text-lg"
              onClick={() => window.location.href = 'mailto:akcarrental01@gmail.com'}
            >
              <Mail className="w-5 h-5 mr-2" />
              akcarrental01@gmail.com
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Logo & Description */}
            <div>
              <img 
                src={COMPANY_LOGO} 
                alt="AK Car Rental" 
                className="h-16 w-auto object-contain mb-4 drop-shadow-[0_0_15px_rgba(255,255,255,0.5)]"
              />
              <p className="text-white/60 text-sm">
                Your trusted partner for insurance replacement vehicles in Hamilton and surrounding areas.
              </p>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="text-white font-semibold mb-4">Contact Us</h4>
              <div className="space-y-3">
                <a href="tel:365-366-2379" className="flex items-center gap-3 text-white/60 hover:text-amber-400 transition-colors">
                  <Phone className="w-4 h-4" />
                  <span>365-366-2379</span>
                </a>
                <a href="mailto:akcarrental01@gmail.com" className="flex items-center gap-3 text-white/60 hover:text-amber-400 transition-colors">
                  <Mail className="w-4 h-4" />
                  <span>akcarrental01@gmail.com</span>
                </a>
                <div className="flex items-start gap-3 text-white/60">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>928 Queenston Rd, Unit 2<br />Hamilton, ON L8G 1B7</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-white font-semibold mb-4">Quick Links</h4>
              <div className="space-y-3">
                <button 
                  onClick={() => navigate('/login')}
                  className="block text-white/60 hover:text-amber-400 transition-colors"
                >
                  Customer Portal
                </button>
                <a href="tel:365-366-2379" className="block text-white/60 hover:text-amber-400 transition-colors">
                  Request a Rental
                </a>
                <a href="mailto:akcarrental01@gmail.com" className="block text-white/60 hover:text-amber-400 transition-colors">
                  Contact Support
                </a>
              </div>
            </div>
          </div>

          <div className="mt-12 pt-8 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} AK Car Rental. All rights reserved. | Insurance Replacement Vehicle Specialists | 24/7 Service | GTA-Wide Delivery
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
